import "./ModelOverview.scss";

import * as R from "ramda";

import {
  ActualVsPredictedDataInitial,
  CptbData,
  ModelOverviewData,
} from "@blisspointmedia/bpm-types/dist/MMM";
import { CONVERSION_FORMATTER_TYPE, currencyFormatter } from "../MMMUtils";
import { DownloadDropdown, Dropdown, DropdownToggleType, InfoTooltip } from "../../Components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { dateToMddyy, getGroupingValue } from "../../utils/date-utils";
import { downloadJSONToCSV, downloadPNG, exportToExcel } from "../../utils/download-utils";
import { formatMoney, formatNumber } from "../../utils/format-utils";

import ActualVsPredicted from "./ActualVsPredicted";
import AreaChart from "../../Components/Charts/AreaChart";
import ChartContainer from "../../Components/ChartContainer";
import { CompanyFiscalCal } from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import DynamicModelOverview from "../../Components/DynamicModelOverview/DynamicModelOverview";
import { TEST_COMPANIES } from "@blisspointmedia/bpm-types/dist/TestCompanies";
import WidgetContainer from "../../Components/WidgetContainer";
import { cptbAreaData } from "./DataFormatters";
import { isInternalSelector } from "../../redux/user";
import { useCompanyInfo } from "../../redux/company";
import { useSelector } from "react-redux";

interface ModelOverviewProps {
  company: string;
  cptbDataDirect: CptbData[];
  cptbData: CptbData[];
  disabledPlatform: boolean | undefined;
  groupBy: string;
  kpi: string;
  kpiType: string;
  modelOverviewData: ModelOverviewData[];
  overallRoasCpa: number | undefined;
  actualVsPredictedData: ActualVsPredictedDataInitial[];
  showActualizedData: boolean;
}
export interface ActualVsPredictedData {
  actual_kpi: number;
  date: string;
  predicted_kpi: number;
  train_test: string;
  [key: string]: any;
}

export type DateGroupingKey = "Day" | "Week" | "Month" | "Quarter" | "Year";
export interface DateGroupingOption {
  value: DateGroupingKey;
  label: string;
}
export const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [
  { value: "Week", label: "Weekly" },
  { value: "Month", label: "Monthly" },
  { value: "Quarter", label: "Quarterly" },
];

interface RightData {
  kpi: string;
  mean_spend: number;
  refresh_date: string;
  test_end_date: string;
  test_rsquared: number;
  test_size: number;
  train_start_date: string;
  train_size: number;
  test_mape: number;
  test_mae?: number;
  "test_mae "?: number;
}
interface PrettyNameToDataMap {
  dataKey: string;
  name: string;
  value: string | number;
}

const DATA_KEYS = [
  "kpi",
  "mean_spend",
  "refresh_date",
  "test_end_date",
  "test_rsquared",
  "test_size",
  "train_start_date",
  "train_size",
  "test_mape",
  "test_mae ", // some old files have a space in the key
  "test_mae",
  "test_rsquared",
];

const CHART_HEIGHT = 405;

const ACT_VS_PRED_TITLE = {
  conversion: "KPI Volume",
  revenue: "Revenue",
};

export const ModelOverview: React.FC<ModelOverviewProps> = ({
  company,
  cptbData,
  cptbDataDirect,
  disabledPlatform,
  groupBy,
  kpi,
  kpiType,
  modelOverviewData,
  overallRoasCpa,
  actualVsPredictedData,
  showActualizedData,
}) => {
  const [chartDateGrouping, setChartDateGrouping] = useState("Week");
  const [rightData, setRightData] = useState({} as RightData);
  const isInternal = useSelector(isInternalSelector);
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const [areaChartData, setAreaChartData] = useState(
    {} as { newD: any[]; areas: any[]; groupedAreas: any[] }
  );
  const [kpiChartYAxisWidth, setKpiChartYAxisWidth] = useState(0);
  const [spendChartYAxisWidth, setSpendChartYAxisWidth] = useState(0);
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  // Maintain horizontal alignment between both stacked charts
  const maxYAxisWidth = useMemo(() => Math.max(kpiChartYAxisWidth, spendChartYAxisWidth), [
    kpiChartYAxisWidth,
    spendChartYAxisWidth,
  ]);

  const modelOverviewDataToExport: [string[], (string | number)[]] = useMemo(() => {
    if (!R.isEmpty(rightData) && kpi && overallRoasCpa) {
      const keyToPrettyName: PrettyNameToDataMap[] = [
        {
          dataKey: "train_start_date",
          name: "First model observation",
          value: dateToMddyy(rightData.train_start_date),
        },
        {
          dataKey: "test_end_date",
          name: "Last model observation",
          value: dateToMddyy(rightData.test_end_date),
        },
        {
          dataKey: "mean_spend",
          name: "Average weekly spend",
          value: currencyFormatter.format(rightData.mean_spend, 0),
        },
        {
          dataKey: "kpi",
          name: "Outcome variable",
          value: kpi,
        },
        {
          dataKey: "refresh_date",
          name: "Last refresh date",
          value: dateToMddyy(rightData.refresh_date),
        },
        {
          dataKey: "N/A",
          name: "Train/Test split",
          value: `${(rightData.train_size * 100).toFixed(0)}/${(rightData.test_size * 100).toFixed(
            0
          )}`,
        },
        {
          dataKey: "test_mape",
          name: "Out-of-sample MAPE",
          value: `${rightData.test_mape.toFixed(1)}%`,
        },
        {
          dataKey: "test_mae",
          name: "Out-of-sample MAE",
          value:
            rightData["test_mae "] !== undefined
              ? kpiType === "revenue"
                ? formatMoney(rightData["test_mae "], 0)
                : formatNumber(rightData["test_mae "], 0)
              : rightData.test_mae !== undefined
              ? kpiType === "revenue"
                ? formatMoney(rightData.test_mae, 0)
                : formatNumber(rightData.test_mae, 0)
              : "",
        },
        {
          dataKey: "overallRoasCpa",
          name:
            kpiType === "revenue" ? "Trailing Twelve Months ROAS" : "Trailing Twelve Months CPA",
          value: overallRoasCpa || "",
        },
      ];

      return keyToPrettyName.reduce(
        (acc, cv) => {
          acc[0].push(cv.name);
          acc[1].push(cv.value);
          return acc;
        },
        [[], []] as [string[], (string | number)[]]
      );
    } else {
      return [[], []];
    }
  }, [rightData, kpi, kpiType, overallRoasCpa]);

  useEffect(() => {
    if (!R.isEmpty(cptbDataDirect)) {
      let data;
      data = R.filter(R.compose(R.not, R.propEq("name", "Covariates")))(cptbDataDirect);
      data = cptbAreaData({
        data,
        data1Key: cptbDataDirect[0].raw_spend !== undefined ? "raw_spend" : "direct_spend",
        groupBy,
        dateGrouping: chartDateGrouping,
        disabledPlatform,
        companyEqualsTest,
        fiscalCal,
      });

      setAreaChartData(data);
    }
  }, [
    chartDateGrouping,
    companyEqualsTest,
    cptbDataDirect,
    disabledPlatform,
    groupBy,
    showActualizedData,
    cptbData,
    fiscalCal,
  ]);

  useEffect(() => {
    if (!R.isEmpty(modelOverviewData)) {
      const rightUsedData = DATA_KEYS.map(key => {
        return modelOverviewData.find(data => {
          return key === data.parameter;
        });
      });

      let rightDataAsObject = {};
      rightUsedData.forEach(data => {
        if (data) {
          rightDataAsObject[data.parameter] = data.value;
        }
      });
      setRightData(rightDataAsObject as any);
    }
  }, [modelOverviewData]);

  const excelDownloadRight = useCallback(() => {
    exportToExcel(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const csvDownloadRight = useCallback(() => {
    downloadJSONToCSV(modelOverviewDataToExport, `${company}_model_overview`);
  }, [modelOverviewDataToExport, company]);

  const excelDownloadActVPred = useCallback(() => {
    exportToExcel(actualVsPredictedData, `${company}_actual_vs_predicted`);
  }, [actualVsPredictedData, company]);

  const pngDownloadActVPred = useCallback(async () => {
    await downloadPNG(".actualVsPredictedGraph", `${company}_actual_vs_predicted`);
  }, [company]);

  const excelDownloadAreaGraph = useCallback(() => {
    const XLSX_KEYS = [
      "client",
      "kpi",
      "refresh_date",
      "name",
      "date",
      "raw_spend",
      "direct_spend",
    ];
    const cptbDataDirectToExport = R.map(R.pick(XLSX_KEYS), cptbDataDirect);

    exportToExcel(cptbDataDirectToExport, `${company}_area_spend`);
  }, [cptbDataDirect, company]);

  const pngDownloadAreaGraph = useCallback(async () => {
    await downloadPNG(".modelOverview .areaChart", `${company}_area`);
  }, [company]);

  const getActVPredDataByDateGrouping = (
    data: any[],
    dateGrouping: string,
    fiscalCal: CompanyFiscalCal
  ) => {
    let totalsByDate: Record<string, Record<string, number>> = {};
    for (let item of data) {
      const { date, actual_kpi, predicted_kpi } = item;

      const dateToUse = getGroupingValue(date, dateGrouping as DateGroupingKey, fiscalCal);
      totalsByDate = R.mergeDeepRight(totalsByDate, {
        [dateToUse]: {
          actual_kpi: R.pathOr(0, [dateToUse, actual_kpi], totalsByDate) + actual_kpi,
          predicted_kpi: R.pathOr(0, [dateToUse, predicted_kpi], totalsByDate) + predicted_kpi,
          train_test: item.train_test,
        },
      });
    }
    let rows: {
      date: string;
      actual_kpi: number;
      predicted_kpi: number;
      train_test: string;
    }[] = [];
    Object.keys(totalsByDate).forEach(item => {
      rows.push({
        date: item,
        actual_kpi: totalsByDate[item].actual_kpi,
        predicted_kpi: totalsByDate[item].predicted_kpi,
        train_test: String(totalsByDate[item].train_test),
      });
    });

    return rows;
  };

  const actVPredDataByDate = getActVPredDataByDateGrouping(
    actualVsPredictedData,
    chartDateGrouping,
    fiscalCal
  );

  return (
    <WidgetContainer
      collapsible
      header="How well does the model understand our business?"
      subHeader={
        <>
          High out-of-sample prediction accuracy is indicative of reliable causal understanding.
          <InfoTooltip rightLabel="More info" size="sm">
            The model is estimated by splitting up the time series data into slices called “train”
            and “test.” The training data is used to estimate the relationships between inputs
            (media investments, contextual variables) and outputs (the KPI). The test data is used
            to evaluate the performance of the trained model “out of sample,” i.e. on observations
            it has not already encountered.
            <br />
            <br /> Out-of-sample predictive accuracy is a critical concept because it proxies what
            we ultimately care about, namely an understanding of likely KPI outputs given a
            combination of novel media inputs. See the out-of-sample statistics at right for
            measures of this concept.
          </InfoTooltip>
        </>
      }
    >
      {!R.isEmpty(rightData) && (
        <div className="modelOverview">
          <div className="left">
            <ChartContainer
              enableHoverDesign
              rightActions={
                <DownloadDropdown
                  size="sm"
                  onClickOptions={[excelDownloadActVPred, pngDownloadActVPred]}
                />
              }
              title={
                <>
                  <Dropdown
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={chartDateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={option => setChartDateGrouping(option)}
                  />
                  <div>{`Historical ${ACT_VS_PRED_TITLE[kpiType]}`}</div>
                  <div className="headerText">(Actual vs Predicted)</div>
                </>
              }
            >
              {!R.isEmpty(actVPredDataByDate) && (
                <ActualVsPredicted
                  data={actVPredDataByDate}
                  dateGrouping={chartDateGrouping}
                  legendValue={ACT_VS_PRED_TITLE[kpiType]}
                  syncId={1}
                  tooltipFormatter={val => {
                    return val < 1000
                      ? CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)
                      : CONVERSION_FORMATTER_TYPE[kpiType].format(val, 1);
                  }}
                  yAxisFormatter={val => CONVERSION_FORMATTER_TYPE[kpiType].format(val, 0)}
                  height="216px"
                  yAxisStaticWidth={maxYAxisWidth}
                  setChartYAxisDynamicWidth={setKpiChartYAxisWidth}
                />
              )}
            </ChartContainer>
            <ChartContainer
              enableHoverDesign
              title={
                <>
                  <Dropdown
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={chartDateGrouping}
                    options={DATE_GROUPING_OPTIONS}
                    onChange={option => setChartDateGrouping(option)}
                  />
                  <div>Historical Spend</div>
                </>
              }
              rightActions={
                <DownloadDropdown
                  size="sm"
                  onClickOptions={[excelDownloadAreaGraph, pngDownloadAreaGraph]}
                />
              }
              height={CHART_HEIGHT}
            >
              {!R.isEmpty(areaChartData) && (
                <div className="modelOverviewSpend">
                  <AreaChart
                    areas={areaChartData.areas}
                    data={areaChartData.newD}
                    dateGrouping={chartDateGrouping}
                    groupedAreas={areaChartData.groupedAreas}
                    incrementality={false}
                    syncId={1}
                    tooltipFormatter={val => {
                      return val < 1000
                        ? currencyFormatter.format(val, 0)
                        : currencyFormatter.format(val, 1);
                    }}
                    xAxisDataKey="date"
                    xAxisTickFormatter={val => dateToMddyy(val)}
                    yAxisTickFormatter={val => `${currencyFormatter.format(val, 0)}`}
                    yAxisStaticWidth={maxYAxisWidth}
                    setChartYAxisDynamicWidth={setSpendChartYAxisWidth}
                  />
                </div>
              )}
            </ChartContainer>
          </div>
          <div className="right">
            <DynamicModelOverview
              sections={[
                {
                  title: "Training Data",
                  rows: [
                    {
                      label: "First model observation",
                      value: dateToMddyy(rightData.train_start_date),
                    },
                    {
                      label: "Last model observation",
                      value: dateToMddyy(rightData.test_end_date),
                    },
                    {
                      label: "Average weekly spend",
                      value: currencyFormatter.format(rightData.mean_spend, 0),
                    },
                    { label: "Outcome variable", value: kpi.replace(/_/g, " ") },
                  ],
                },
                {
                  title: "Recency",
                  rows: [
                    { label: "Last refresh date", value: dateToMddyy(rightData.refresh_date) },
                  ],
                },
                {
                  title: "Statistics",
                  rows: [
                    {
                      label: "Train/Test split",
                      value: `${(rightData.train_size * 100).toFixed(0)}/${(
                        rightData.test_size * 100
                      ).toFixed(0)}`,
                      tooltipText:
                        "This refers to the fraction of data used to fit the model vs the fraction held out for model validation. Setting aside data for model validation is very important in ensuring that the model generalizes well to new, unseen data.",
                    },
                    ...(isInternal && !companyEqualsTest
                      ? [
                          {
                            label: (
                              <>
                                Out-of-sample R<sup>2</sup>
                              </>
                            ),
                            value: rightData.test_rsquared.toFixed(2),
                            tooltipText:
                              "The percentage of variation in the unseen test data outcomes that is accounted for by the model's explanatory variables. Formally, this is 1 - (residual sum of squares) / (total sum of squares)",
                          },
                        ]
                      : []),
                    {
                      label: "Out-of-sample MAPE",
                      value: `${rightData.test_mape.toFixed(1)}%`,
                      tooltipText:
                        "Mean absolute percentage error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in percentage terms. Its definition is avg(abs((predicted - actual)/actual)).",
                    },
                    {
                      label: "Out-of-sample MAE",
                      value:
                        rightData["test_mae "] !== undefined
                          ? kpiType === "revenue"
                            ? formatMoney(rightData["test_mae "], 0)
                            : formatNumber(rightData["test_mae "], 0)
                          : rightData.test_mae !== undefined
                          ? kpiType === "revenue"
                            ? formatMoney(rightData.test_mae, 0)
                            : formatNumber(rightData.test_mae, 0)
                          : "",
                      tooltipText:
                        "Mean absolute error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in absolute terms. Its definition is avg(abs(predicted - actual)).",
                    },
                  ],
                },
                {
                  title: "Key Results",
                  rows: [
                    {
                      label:
                        kpiType === "revenue"
                          ? "Trailing Twelve Months ROAS"
                          : "Trailing Twelve Months CPA",
                      value: overallRoasCpa
                        ? kpiType === "conversion"
                          ? formatMoney(overallRoasCpa)
                          : formatNumber(overallRoasCpa, 2)
                        : "--",
                    },
                  ],
                },
              ]}
              rightActions={
                <DownloadDropdown
                  size="sm"
                  onClickOptions={[excelDownloadRight, csvDownloadRight]}
                  menuOptions={["XLSX", "CSV"]}
                />
              }
            />
          </div>
        </div>
      )}
    </WidgetContainer>
  );
};

export default ModelOverview;
